import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Box, Menu, MenuItem, IconButton, Avatar, Button, Typography, Divider } from '@mui/material';
import { useRouter, Link, useLocation } from '@tanstack/react-router';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { getUserInitials, useCurrentUserQuery } from 'entities/user';
import { Routes } from 'shared/routes';
import { LogoIcon } from 'shared/ui';

import { useLogoutMutation } from './logoutApi';

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 40px',
    height: '64px',
    boxSizing: 'border-box',
    width: '100%',
    borderBottom: '1px solid rgba(98, 111, 136, 0.56)',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    mr: 2,
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  navButton: {
    textTransform: 'none',
    color: '#626F88',
    fontWeight: 500,
    fontSize: '14px',
    padding: '8px 16px',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: 'rgba(85, 19, 205, 0.04)',
      color: 'primary.main',
    },
  },
  activeNavButton: {
    backgroundColor: 'rgba(85, 19, 205, 0.08)',
    color: 'primary.main',
  },
  accountAvatarContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  accountAvatar: {
    width: 24,
    height: 24,
    background: '#AF93CA',
    fontSize: 10,
    fontWeight: 600,
  },
  button: {
    textTransform: 'none',
    minWidth: 0,
    width: '208px',
  },
  buttonText: {
    color: '#626F88',
  },
  arrowDropDownIcon: {
    color: '#626F88',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  notificationIcon: {
    color: '#3C4856',
    fontSize: 24,
  },
  userIconButton: {
    padding: 0,
  },
  avatar: {
    width: 32,
    height: 32,
    cursor: 'pointer',
    boxShadow: '0px 2px 8px rgba(55, 125, 255, 0.15)',
    backgroundColor: 'transparent',
    border: '1px solid #6C7680',
    color: '#9FACBD',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 400,
  },
  menuItem: {
    fontSize: '14px',
    padding: '8px 16px',
  },
  userEmail: {
    fontSize: '12px',
    color: '#626F88',
    fontWeight: 400,
    padding: '8px 16px',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
};

const Header = () => {
  const { data: currentUser } = useCurrentUserQuery();
  const router = useRouter();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleUserClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleUserClose = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    handleUserClose();
    try {
      await logout().unwrap();
      enqueueSnackbar('Logged out successfully', { variant: 'success' });
      router.navigate({ to: Routes.frontend.loginPath });
    } catch {
      enqueueSnackbar('Logout failed. Please try again.', { variant: 'error' });
    }
  };

  const initials = currentUser ? getUserInitials(currentUser) : '';

  const navigationItems = [
    { label: 'MCPs', path: Routes.frontend.mcpsPath },
    { label: 'Your Rules', path: Routes.frontend.rulesPath },
    { label: 'Community Rules', path: Routes.frontend.communityRulesPath },
  ];

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Box component="header" sx={styles.header}>
      <Box sx={styles.left}>
        <Link to={Routes.frontend.rootPath} style={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={styles.logo}>
            <LogoIcon height={32} />
          </Box>
        </Link>

        <Box sx={styles.navigation}>
          {navigationItems.map((item) => (
            <Button
              key={item.path}
              component={Link}
              to={item.path}
              variant="text"
              sx={{
                ...styles.navButton,
                ...(isActiveRoute(item.path) ? styles.activeNavButton : {}),
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        <div id="breadcrumbs-portal" style={{ flex: 1 }} />
      </Box>
      <Box sx={styles.right}>
        <IconButton color="inherit">
          <NotificationsNoneIcon sx={styles.notificationIcon} />
        </IconButton>

        <IconButton onClick={handleUserClick} sx={styles.userIconButton}>
          <Avatar sx={styles.avatar}>{initials || '?'}</Avatar>
        </IconButton>
        <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleUserClose}>
          <Box sx={styles.userEmail}>
            <Typography variant="body2" sx={{ fontSize: '12px', color: '#626F88' }}>
              {currentUser?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleLogout} sx={styles.menuItem}>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Header;
